class SineWaveArrow extends HTMLElement {
  constructor() {
    super();

    this.offset = 0;
    this.isAnimating = false;
    this.animationFrame = null;
    this.width = 0;
    this.resizeObserver = null;
    this.mouseX = 0;

    this.currentFrequency = 0;
    this.currentAmplitude = 0;
    this.transitionStartTime = 0;
    this.transitionDuration = 350;

    this.innerHTML = `
      <svg>
        <path class="mirror" />
        <path class="primary" />
      </svg>
      <div class="arrow-head"></div>
    `;

    this.svg = this.querySelector('svg');
    this.primaryPath = this.querySelector('.primary');
    this.mirrorPath = this.querySelector('.mirror');

    this.startAnimation = this.startAnimation.bind(this);
    this.stopAnimation = this.stopAnimation.bind(this);
    this.animate = this.animate.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);

    this.addEventListener('mouseenter', this.startAnimation);
    this.addEventListener('mouseleave', this.stopAnimation);
    this.addEventListener('mousemove', this.handleMouseMove);
    this.addEventListener('focus', this.startAnimation);
    this.addEventListener('blur', this.stopAnimation);
  }

  handleMouseMove(event) {
    const rect = this.getBoundingClientRect();
    this.mouseX = event.clientX - rect.left;
  }

  connectedCallback() {
    this.resizeObserver = new ResizeObserver(this.updateDimensions);
    this.resizeObserver.observe(this);
    this.updateDimensions();
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  updateDimensions() {
    const rect = this.getBoundingClientRect();
    this.width = rect.width;
    this.svg.setAttribute('viewBox', `0 0 ${this.width} 150`);
    if (!this.isAnimating) {
      const straightLine = `M0,75 L${this.width},75`;
      this.primaryPath.setAttribute('d', straightLine);
      this.mirrorPath.setAttribute('d', straightLine);
    }
  }

  static get observedAttributes() {
    return ['frequency', 'amplitude', 'min-speed', 'max-speed'];
  }

  get frequency() {
    return Number(this.getAttribute('frequency')) || 2.25;
  }

  get amplitude() {
    return Number(this.getAttribute('amplitude')) || 0.2;
  }

  get minSpeed() {
    return Number(this.getAttribute('min-speed')) || 0.05;
  }

  get maxSpeed() {
    return Number(this.getAttribute('max-speed')) || 0.2;
  }

  getCurrentSpeed() {
    if (!this.isAnimating || !this.width) return this.minSpeed;
    const normalizedX = Math.max(0, Math.min(1, this.mouseX / this.width));
    return this.minSpeed + (this.maxSpeed - this.minSpeed) * normalizedX;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isAnimating) {
      this.transitionStartTime = performance.now();
      this.currentFrequency = 0;
      this.currentAmplitude = 0;
    }
  }

  updateTransitionValues(currentTime) {
    const elapsed = currentTime - this.transitionStartTime;
    const progress = Math.min(elapsed / this.transitionDuration, 1);
    const eased = 1 - (1 - progress) * (1 - progress);

    this.currentFrequency = this.frequency * eased;
    this.currentAmplitude = this.amplitude * eased;
  }

  calculateY(x, mirror = false) {
    if (!this.width || isNaN(x)) return 0;
    
    const distanceFromMouse = Math.abs(x - this.mouseX);
    const maxDistance = this.width / 2;
    const distanceFromEnd = (this.width - x) / this.width;
    const endDampening = Math.pow(Math.max(0, Math.min(1, distanceFromEnd)), 0.5);
    const mouseEffect = Math.exp(-(distanceFromMouse * distanceFromMouse) / (2 * maxDistance * maxDistance));
    const amplitudeModifier = mouseEffect * endDampening;

    const amplitude = mirror ? -this.currentAmplitude : this.currentAmplitude;
    const y = Math.sin(Math.sqrt(Math.max(0, x) * this.currentFrequency) + this.offset) * 
              amplitudeModifier * 
              (0.1 * amplitude * this.width);
              
    return isNaN(y) ? 0 : y;
  }

  createWavePath() {
    let primaryPath = ['M', 0, 75];
    let mirrorPath = ['M', 0, 75];

    for (let x = 0; x <= this.width; x++) {
      const y1 = 75 - this.calculateY(x);
      const y2 = 75 - this.calculateY(x, true);
      primaryPath.push('L', x, y1);
      mirrorPath.push('L', x, y2);
    }

    this.primaryPath.setAttribute('d', primaryPath.join(' '));
    this.mirrorPath.setAttribute('d', mirrorPath.join(' '));
  }

  startAnimation() {
    this.isAnimating = true;
    this.transitionStartTime = performance.now();
    this.currentFrequency = 0;
    this.currentAmplitude = 0;
    this.animate();
  }

  stopAnimation() {
    this.isAnimating = false;
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    this.currentFrequency = 0;
    this.currentAmplitude = 0;
    const straightLine = `M0,75 L${this.width},75`;
    this.primaryPath.setAttribute('d', straightLine);
    this.mirrorPath.setAttribute('d', straightLine);
  }

  animate(currentTime = performance.now()) {
    if (!this.isAnimating) return;

    this.updateTransitionValues(currentTime);
    this.offset += this.getCurrentSpeed();
    this.createWavePath();
    this.animationFrame = requestAnimationFrame(this.animate);
  }
}

customElements.define('sine-wave-arrow', SineWaveArrow);
